import { useQueryClient } from '@tanstack/react-query'
import { useCallback, useState, useTransition } from 'react'

import { TGetEmailVerificationParams, TSession } from '../actions/auth/auth-actions.type'
import requestEmailVerification from '../actions/auth/get-email-verification'
import getSession from '../actions/auth/get-session'
import postEmailVerificationOtp from '../actions/auth/post-verify-email-otp'
import setSession from '../actions/auth/set-session'
import { USER_PROFILE_KEY } from '../apis/queries/use-suspense-get-detail-profile'
import { QUERY_INFO_KEY } from '../apis/queries/use-suspense-get-query-info'
import {
  clearVerifyEmailState,
  markAsVerifyEmailAttempt,
  markAsVerifyEmailInputRateLimitReached,
  markAsVerifyEmailLimitReached,
  resetVerifyEmailLimitReached,
  setCurrentVerifyEmail,
} from '../store/slice/email-otp-slice'
import { setCurrentUserEmail, setCurrentUserUsername } from '../store/slice/user-slice'
import { EApiErrorCode } from '../types/global.types'

import { useAppDispatch } from './use-app-dispatch'
import { useAppSelector } from './use-app-selector'
import useToast from './use-toast'

const useEmailVerify = () => {
  const dispatch = useAppDispatch()
  const toast = useToast()
  const queryClient = useQueryClient()

  const { email: currentEmailOtp } = useAppSelector((state) => state.emailOtp)
  const {
    lastResendAttemptTime,
    rateLimitLockedTime,
    isRateLimitReached,
    attempt,
    isInputRateLimitReached,
  } = useAppSelector((state) => state.emailOtp)

  const [otp, setOtp] = useState<string>()
  const [error, setError] = useState<string | undefined>()
  const [isRequestingOtp, starRequestingOtpTransition] = useTransition()
  const [isVerifying, startVerifyingTransition] = useTransition()
  const [successVerifyOtp, setSuccessVerifyOtp] = useState(false)

  const requestEmailOtp = async ({ email, isUpdate, withOtp }: TGetEmailVerificationParams) => {
    starRequestingOtpTransition(async () => {
      const result = await requestEmailVerification({ email, isUpdate, withOtp })
      dispatch(setCurrentVerifyEmail(email))

      if (result.success) {
        if (attempt > 1) {
          toast.show({
            title: 'Resend OTP Success',
            description: 'A new verification code has been successfully sent to your email.',
            type: 'success',
          })
        }

        dispatch(markAsVerifyEmailAttempt())
        dispatch(resetVerifyEmailLimitReached())

        return
      }

      if (!result.success && result?.error?.code === EApiErrorCode.OTP_REQUEST_LIMIT_EXCEEDED) {
        toast.show({
          title: 'OTP Request Rate Limit Exceeded',
          description: result?.error?.message || 'Oops something went wrong.',
          traceId: result?.traceId || '',
          type: 'error',
        })

        dispatch(markAsVerifyEmailLimitReached({ remainingTime: result?.result?.remainingSeconds }))
        return
      }

      toast.show({
        title: result?.error?.code || 'ERROR OCCURED',
        description: result?.error?.message || 'Oops something went wrong.',
        traceId: result?.traceId || '',
        type: 'error',
      })
    })
  }

  const onOtpChange = useCallback((otpCode: string) => {
    setError(undefined)
    setOtp(otpCode)
  }, [])

  const onVerifyOtp = async (option: { isUpdate?: boolean } = { isUpdate: false }) => {
    if (!otp || otp.length < 6) {
      setError('Invalid OTP Code')
    } else {
      startVerifyingTransition(async () => {
        const response = await postEmailVerificationOtp({
          email: currentEmailOtp?.toLowerCase(),
          otp,
          isUpdate: option.isUpdate,
        })

        queryClient.invalidateQueries({ queryKey: [USER_PROFILE_KEY] })

        queryClient.invalidateQueries({
          queryKey: [QUERY_INFO_KEY, currentEmailOtp?.toLowerCase()],
        })

        if (response.success) {
          dispatch(setCurrentUserEmail(currentEmailOtp))
          dispatch(setCurrentUserUsername(currentEmailOtp))

          const session = await getSession()

          await setSession({
            ...session,
            username: currentEmailOtp,
          } as TSession)

          setSuccessVerifyOtp(true)
          dispatch(clearVerifyEmailState())

          return
        }

        toast.show({
          title: response?.error?.code || 'ERROR OCCURED',
          description: response?.error?.message || 'Oops something went wrong.',
          traceId: response?.traceId || '',
          type: 'error',
        })

        if (!response.success && response?.error?.code === EApiErrorCode.OTP_INPUT_LIMIT_EXCEEDED) {
          dispatch(
            markAsVerifyEmailInputRateLimitReached({
              remainingTime: response?.result?.remainingSeconds,
            }),
          )

          return
        }
      })
    }
  }

  return {
    currentEmail: currentEmailOtp,
    onOtpChange,
    lastResendAttemptTime,
    rateLimitLockedTime,
    requestEmailOtp,
    isRateLimitReached,
    isInputRateLimitReached,
    onVerifyOtp,
    isVerifying,
    isRequestingOtp,
    successVerifyOtp,
    otp,
    error,
  }
}

export default useEmailVerify
